/**
 * Configuration des routes de l'application
 * Architecture Features-First
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';

// Layouts
import { RootLayout } from './layouts/RootLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { UserLayout } from './layouts/UserLayout';
import { PaieLayout } from './layouts/PaieLayout';
import { EspaceSalarieLayout } from './layouts/EspaceSalarieLayout';

// Espace salarié pages
import { MesBulletinsPage } from '../Modules/espace-salarie/pages/MesBulletinsPage';
import { MonBulletinDetailPage } from '../Modules/espace-salarie/pages/MonBulletinDetailPage';
import { MonProfilPage } from '../Modules/espace-salarie/pages/MonProfilPage';

// Auth Pages
import {
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  TwoFactorPage,
  TwoFactorSetupPage,
  VerifyEmailPage,
} from '@features/auth';

// Admin Pages
import {
  DashboardPage,
  UsersPage,
  UserDetailPage,
  RolesListPage,
  CreerRolePage,
  ModifierRolePage,
  SecurityPage,
  ActivityPage,
  SettingsPage,
} from '@features/user-admin';

// ─── Pages Paie ───────────────────────────────────────────────────────────────
import {
  BulletinsListePage,
  BulletinGenerationPage,
  BulletinsMassePage,
  JobSuiviPage,
  TableauEtatRemunerationPage,
  BulletinDetailPage,
} from '@bulletins';
import { EmployesListePage, EmployeFormPage, EmployeImportPage, EmployeImportExcelPage, EmployeDetailPage } from '@employes';
import { ConfigurationDashboardPage, SimulateurPage, BaremesListePage, BaremeFormPage, ExportComptablePage, CataloguePrimesPage, TaxesTemporairesPage, ParametresSystemePage, ThemePage, OrganisationPage } from "@configuration";
import { DeclarationsListePage } from '@declarations';
import { RapportsDashboardPage, LivrePaiePage, RapportAuditPage } from '@rapports';
import { ProjetsListePage, ProjetDetailPage } from '@administration';
import { ContributionsEmployePage, ContributionsProjetPage } from '../Modules/contributionsProjet/pages';

// Protected Route Component
import { ProtectedRoute } from './components/ProtectedRoute';
import { UserDashboard } from './components/UserDashboard';

// App Pages
import { AccesRefusePage } from './pages/AccesRefusePage';
import LandingPage from './pages/LandingPage';

// User Settings Pages
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { TwoFactorSettingsPage } from './pages/TwoFactorSettingsPage';
import { PasswordSettingsPage } from './pages/PasswordSettingsPage';
import { SessionsSettingsPage } from './pages/SessionsSettingsPage';
import { SecuritySettingsPage } from './pages/SecuritySettingsPage';
import { HistoryPage } from './pages/HistoryPage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Landing page publique
      {
        index: true,
        element: <LandingPage />,
      },

      // Auth routes (public)
      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'register',
            element: <RegisterPage />,
          },
          {
            path: 'reset-password',
            element: <ResetPasswordPage />,
          },
          {
            path: '2fa',
            element: <TwoFactorPage />,
          },
          {
            path: '2fa-setup',
            element: <TwoFactorSetupPage />,
          },
        ],
      },

      // Admin routes (protected)
      {
        path: 'admin',
        element: (
          <ProtectedRoute requiredRole="admin">
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: 'users',
            element: <UsersPage />,
          },
          {
            path: 'users/new',
            element: <Navigate to="/admin/users" replace />,
          },
          {
            path: 'users/:id',
            element: <UserDetailPage />,
          },
          {
            path: 'roles',
            element: <RolesListPage />,
          },
          {
            path: 'roles/new',
            element: <CreerRolePage />,
          },
          {
            path: 'roles/:idRole/edit',
            element: <ModifierRolePage />,
          },
          {
            path: 'security',
            element: <SecurityPage />,
          },
          {
            path: 'activity',
            element: <ActivityPage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
        ],
      },

      // ─── Routes Gestion de Paie ─────────────────────────────────────────────
      {
        path: 'paie',
        element: (
          <ProtectedRoute requiredRole="paie">
            <PaieLayout />
          </ProtectedRoute>
        ),
        children: [
          // Bulletins — routes statiques AVANT routes dynamiques
          { path: 'bulletins',                          element: <BulletinsListePage /> },
          { path: 'bulletins/generer',                  element: <BulletinGenerationPage /> },
          { path: 'bulletins/masse',                    element: <BulletinsMassePage /> },
          { path: 'bulletins/jobs/:jobId',              element: <JobSuiviPage /> },
          { path: 'bulletins/tableau/:annee/:mois',     element: <TableauEtatRemunerationPage /> },
          { path: 'bulletins/:idBulletin',              element: <BulletinDetailPage /> },
          // Employés — routes statiques AVANT routes dynamiques
          { path: 'employes',                           element: <EmployesListePage /> },
          { path: 'employes/nouveau',                   element: <EmployeFormPage /> },
          { path: 'employes/importer-excel',            element: <EmployeImportExcelPage /> },
          { path: 'employes/import',                    element: <EmployeImportPage /> },
          { path: 'employes/:id/modifier',              element: <EmployeFormPage /> },
          { path: 'employes/:id',                       element: <EmployeDetailPage /> },
          // Configuration
          { path: 'configuration',                      element: <ConfigurationDashboardPage /> },
          { path: 'configuration/parametres',           element: <ParametresSystemePage /> },
          { path: 'configuration/simulateur',           element: <SimulateurPage /> },
          { path: 'configuration/baremes',               element: <BaremesListePage /> },
          { path: 'configuration/baremes/nouveau',       element: <BaremeFormPage /> },
          { path: 'configuration/baremes/:id/modifier',  element: <BaremeFormPage /> },
          { path: 'configuration/primes',               element: <CataloguePrimesPage /> },
          { path: 'configuration/taxes',                element: <TaxesTemporairesPage /> },
          { path: 'configuration/theme',               element: <ThemePage /> },
          { path: 'configuration/organisation',        element: <OrganisationPage /> },
          { path: 'rapports/export-comptable',           element: <ExportComptablePage /> },
          // Déclarations
          { path: 'declarations',                       element: <DeclarationsListePage /> },
          // Rapports
          { path: 'rapports',                           element: <RapportsDashboardPage /> },
          { path: 'rapports/livre-paie',                element: <LivrePaiePage /> },
          { path: 'rapports/audit',                     element: <RapportAuditPage /> },
          // Administration — route statique AVANT dynamique
          { path: 'administration/projets',              element: <ProjetsListePage /> },
          { path: 'administration/projets/:id',          element: <ProjetDetailPage /> },
          // Contributions projet — routes statiques AVANT dynamiques
          { path: 'contributions/employe/:id',           element: <ContributionsEmployePage /> },
          { path: 'contributions/projet/:id',            element: <ContributionsProjetPage /> },
        ],
      },

      // ─── Espace salarié ──────────────────────────────────────────────────────
      {
        path: 'espace-salarie',
        element: (
          <ProtectedRoute requiredRole="employe">
            <EspaceSalarieLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <MesBulletinsPage /> },
          { path: 'bulletins/:id', element: <MonBulletinDetailPage /> },
          { path: 'mon-profil', element: <MonProfilPage /> },
        ],
      },

      // Vérification email (lien reçu par mail à l'inscription)
      { path: 'verify-email', element: <VerifyEmailPage /> },

      // Page accès refusé
      { path: 'acces-refuse', element: <AccesRefusePage /> },

      // Dashboard utilisateur (protected - avec UserMenu et settings)
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <UserLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <UserDashboard />,
          },
          {
            path: 'account',
            element: <AccountSettingsPage />,
          },
          {
            path: '2fa',
            element: <TwoFactorSettingsPage />,
          },
          {
            path: 'password',
            element: <PasswordSettingsPage />,
          },
          {
            path: 'sessions',
            element: <SessionsSettingsPage />,
          },
          {
            path: 'security',
            element: <SecuritySettingsPage />,
          },
          {
            path: 'history',
            element: <HistoryPage />,
          },
        ],
      },
    ],
  },
]);

export default router;
